import { useCallback, useRef, useState } from 'react';

const MAX_HISTORY = 20;

interface DocxHistory {
  current: Uint8Array;
  canUndo: boolean;
  canRedo: boolean;
  push: (bytes: Uint8Array) => void;
  undo: () => Uint8Array | null;
  redo: () => Uint8Array | null;
  reset: (bytes: Uint8Array) => void;
}

/**
 * Undo/redo history for the editable .docx (byte snapshots). Mirrors the AI review's
 * useDocumentHistory, but the unit is the whole .docx `Uint8Array` rather than a
 * LegalDocument. Capped at MAX_HISTORY entries to bound memory. The caller layers
 * persistence + re-render on top; this hook only owns the in-memory stack.
 */
export function useDocxHistory(initial: Uint8Array): DocxHistory {
  const [current, setCurrent] = useState<Uint8Array>(initial);
  const pastRef = useRef<Uint8Array[]>([]);
  const futureRef = useRef<Uint8Array[]>([]);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const push = useCallback(
    (bytes: Uint8Array) => {
      setCurrent((prev) => {
        pastRef.current = [...pastRef.current.slice(-(MAX_HISTORY - 1)), prev];
        futureRef.current = [];
        bump();
        return bytes;
      });
    },
    [bump]
  );

  const undo = useCallback((): Uint8Array | null => {
    const past = pastRef.current;
    if (past.length === 0) return null;
    const previous = past[past.length - 1];
    let restored: Uint8Array | null = null;
    setCurrent((prev) => {
      pastRef.current = past.slice(0, -1);
      futureRef.current = [prev, ...futureRef.current];
      restored = previous;
      bump();
      return previous;
    });
    return restored;
  }, [bump]);

  const redo = useCallback((): Uint8Array | null => {
    const future = futureRef.current;
    if (future.length === 0) return null;
    const next = future[0];
    let restored: Uint8Array | null = null;
    setCurrent((prev) => {
      futureRef.current = future.slice(1);
      pastRef.current = [...pastRef.current, prev];
      restored = next;
      bump();
      return next;
    });
    return restored;
  }, [bump]);

  const reset = useCallback(
    (bytes: Uint8Array) => {
      pastRef.current = [];
      futureRef.current = [];
      setCurrent(bytes);
      bump();
    },
    [bump]
  );

  return {
    current,
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    push,
    undo,
    redo,
    reset,
  };
}
